import type { ComponentProps } from "react";
import { useState } from "react";

import { useQuery } from "@tanstack/react-query";

import { Table } from "@similarweb/pro-logic-components";
import { IconButton } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { useSearchAdvertisingDataAndParams } from "app/pages/digital-marketing/ad-intelligence/common/hooks/useSearchAdvertisingDataAndParams";
import { getShareValue, getVolumeValue } from "app/pages/website-analysis/traffic-sources/search/common/utils";
import DurationService from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";
import { allTrackers } from "app/services/track/track";
import { DEVICES_TYPES_FOR_API, EDeviceType } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { translate } from "app/filters/i18nFilter";
import type { ISortProps } from "app/pages/digital-marketing/ad-intelligence/common/utils/utils";
import { buttonTypes } from "app/UtilitiesAndConstants/Constants/ButtonTypes";

import { formatPercentageWithDynamicPrecision } from "../table/changeValueFormatters";
import { SourceTypeMap } from "app/common/keyword-research/website-keywords/tableConsts";
import { useWebsiteKeywordsEndpoints } from "../websiteKeywordsEndpoints";
import type { KeywordTransformedData } from "app/common/keyword-research/website-keywords/websiteKeywordsPageTypes";

import { getColumns } from "./columnsConfig";
import type { ResponseData, TransformedResponseData } from "./types";

const DEFAULT_SORT = "Share";
const ROWS_PER_PAGE = 5;
const DEFAULT_PAGE = 1;

const SORT_KEY_MAP: Record<string, string> = {
    clicksShare: DEFAULT_SORT,
    clicksChange: "ClicksChange",
};
const REVERSE_SORT_KEY_MAP: Record<string, string> = Object.fromEntries(
    Object.entries(SORT_KEY_MAP).map(([uiKey, apiKey]) => [apiKey, uiKey]),
);
export const INITIAL_SORT = { sort: DEFAULT_SORT, asc: false };

interface IWebsiteKeywordsEnrichedProps {
    row: KeywordTransformedData;
    closeExpandedRow: () => void;
}
export const WebsiteKeywordsEnriched: React.FC<IWebsiteKeywordsEnrichedProps> = ({ row, closeExpandedRow }) => {
    const { keyword } = row;
    const { country, duration, pageFilter, key, selectedPageTab, webSource } = useSearchAdvertisingDataAndParams();
    const endpoints = useWebsiteKeywordsEndpoints();

    const durationObj = DurationService.getDurationData(duration);
    const { to, from, isWindow } = durationObj.forAPI;
    const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE);
    const [sort, setSort] = useState<ISortProps>({
        sort: INITIAL_SORT.sort,
        asc: INITIAL_SORT.asc,
    });

    const apiParams = {
        ...sort,
        page: currentPage,
        country,
        keyword,
        to,
        from,
        isWindow,
        pageFilterJson: pageFilter,
        keys: key,
        sourceType: SourceTypeMap[selectedPageTab],
        Device: (webSource === EDeviceType.MOBILE ? DEVICES_TYPES_FOR_API.MOBILE : webSource) as DEVICES_TYPES_FOR_API,
        pageSize: ROWS_PER_PAGE,
    };

    const closeEnrichedRow = () => {
        allTrackers.dispatchTrackEvent("Open", "Click", "Website keywords Drilldown/Collapsed");
        closeExpandedRow();
    };

    const { data, isLoading, isError } = useQuery({
        queryKey: [endpoints.drillDownTable, apiParams],
        queryFn: async () => DefaultFetchService.getInstance().get<ResponseData>(endpoints.drillDownTable, apiParams),
        select: transformEnrichedCellData,
    });

    const onSort: ComponentProps<typeof Table<(typeof data.data)[number]>>["onSort"] = (sorter) => {
        const isAsc = sorter.order === "ascend";
        const columnKey = (sorter.columnKey as string) ?? "";
        const apiSortKey = SORT_KEY_MAP[columnKey] ?? columnKey;
        setSort({
            sort: apiSortKey,
            asc: isAsc,
        });
        setCurrentPage(DEFAULT_PAGE);
    };

    const noDataObject = isError
        ? {
              title: translate("keyword.research.search.ads.table.error.title"),
              subtitle: translate("keyword.research.search.ads.table.error.subtitle"),
          }
        : {
              title: translate("keyword.research.search.ads.table.no.data.title"),
              subtitle: translate("keyword.research.search.ads.table.no.data.subtitle"),
          };

    return (
        <>
            <Flex
                data-automation="url-cell-enriched-row"
                justifyContent="flex-end"
                alignItems="center"
                padding="0 20px 8px 24px"
            >
                <IconButton
                    type={buttonTypes.FLAT}
                    onClick={closeEnrichedRow}
                    iconName="clear"
                    placement="left"
                    iconSize="sm"
                />
            </Flex>
            <Table<(typeof data.data)[number]>
                columnConfig={getColumns(selectedPageTab, country, {
                    sort: REVERSE_SORT_KEY_MAP[sort.sort] ?? sort.sort,
                    asc: sort.asc,
                })}
                data={data?.data ?? []}
                isLoading={isLoading}
                loaderRowCount={ROWS_PER_PAGE}
                noData={noDataObject}
                size="large"
                pagination={{
                    pageSize: ROWS_PER_PAGE,
                    total: data?.TotalCount ?? 0,
                    current: currentPage,
                    onChange: (_, __, newPage) => setCurrentPage(newPage),
                }}
                sticky={false}
                onSort={onSort}
            />
        </>
    );
};

const transformEnrichedCellData = (data: ResponseData): TransformedResponseData => {
    return {
        ...data,
        data: data.Data.map((item) => ({
            ...item,
            SiteSerpFeatures: { [item.Url]: item.SerpFeatures },
            SearchTerm: item.Url,
            position: { position: [{ Value: item.Position }] },
            share: getShareValue(item.Share),
            clicksShare: {
                totalClicks: getVolumeValue(item.Clicks),
                value: getShareValue(item.Share),
            },
            clicksChange: {
                change: item.Change,
                changeState: item.ChangeState,
                changeValueFormatted: formatPercentageWithDynamicPrecision(item.Change),
            },
        })),
    };
};
